import { supabase } from '@/lib/supabase';

export type DailyOrderMetric = {
  day: string;
  order_count: number;
  total_amount: number;
};

export type DailyVisitMetric = {
  day: string;
  site_visits: number;
  product_views: number;
};

export type DailyCombinedMetric = {
  day: string;
  orderCount: number;
  orderTotalAmount: number;
  siteVisits: number;
  productViews: number;
};

function toNumber(value: unknown) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

export async function getDailyOrderMetrics(options?: {
  days?: number;
  timezone?: string;
  branchId?: string;
}): Promise<DailyOrderMetric[]> {
  if (!supabase) return [];

  const { data, error } = await (supabase as any).rpc('get_daily_order_metrics', {
    p_days: options?.days ?? 14,
    p_timezone: options?.timezone ?? 'Africa/Nairobi',
    p_branch_id: options?.branchId ?? null,
  });

  if (error) {
    throw new Error(error.message || 'Failed to load daily order metrics');
  }

  return (Array.isArray(data) ? data : []).map((row: any) => ({
    day: String(row.day || ''),
    order_count: toNumber(row.order_count),
    total_amount: toNumber(row.total_amount),
  }));
}

export async function getDailyVisitMetrics(options?: {
  days?: number;
  timezone?: string;
}): Promise<DailyVisitMetric[]> {
  if (!supabase) return [];

  const { data, error } = await (supabase as any).rpc('get_daily_visit_metrics', {
    p_days: options?.days ?? 14,
    p_timezone: options?.timezone ?? 'Africa/Nairobi',
  });

  if (error) {
    throw new Error(error.message || 'Failed to load daily visit metrics');
  }

  return (Array.isArray(data) ? data : []).map((row: any) => ({
    day: String(row.day || ''),
    site_visits: toNumber(row.site_visits),
    product_views: toNumber(row.product_views),
  }));
}

export async function getDailyCombinedMetrics(options?: {
  days?: number;
  timezone?: string;
  branchId?: string;
}): Promise<DailyCombinedMetric[]> {
  const [orders, visits] = await Promise.all([
    getDailyOrderMetrics({ days: options?.days, timezone: options?.timezone, branchId: options?.branchId }).catch(
      () => []
    ),
    getDailyVisitMetrics({ days: options?.days, timezone: options?.timezone }).catch(() => []),
  ]);

  const byDay = new Map<string, DailyCombinedMetric>();

  for (const row of visits) {
    byDay.set(row.day, {
      day: row.day,
      orderCount: 0,
      orderTotalAmount: 0,
      siteVisits: row.site_visits,
      productViews: row.product_views,
    });
  }

  for (const row of orders) {
    const current =
      byDay.get(row.day) ||
      ({
        day: row.day,
        orderCount: 0,
        orderTotalAmount: 0,
        siteVisits: 0,
        productViews: 0,
      } satisfies DailyCombinedMetric);
    current.orderCount = row.order_count;
    current.orderTotalAmount = row.total_amount;
    byDay.set(row.day, current);
  }

  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

